import React from 'react';
import { Check, BookOpen, Clock, Shield, Users, AlertTriangle, Lightbulb } from 'lucide-react';
import { CTAButton } from './components/CTAButton';
import { TestimonialCard } from './components/TestimonialCard';
import { BonusCard } from './components/BonusCard';
import { SocialProofPopup } from './components/SocialProofPopup';
export function App() {
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-0">
      <SocialProofPopup data-id="element-1" />

      {/* HERO SECTION */}
      <header className="bg-gradient-to-b from-blue-950 to-blue-900 text-white pt-12 pb-20 px-4 relative overflow-hidden" data-id="element-2">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-3"></div>
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-4">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-5">
            <div className="flex-1 text-center md:text-left" data-id="element-6">
              <div className="inline-block bg-blue-800/50 border border-blue-700 rounded-full px-4 py-1 mb-6 text-sm font-medium text-blue-200" data-id="element-7">
                Para docentes de nivel secundario
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 tracking-tight" data-id="element-8">
                Micro y Macro{' '}
                <span className="text-amber-400" data-id="element-9">Economía 360</span>
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed" data-id="element-10">
                Todo el año de micro y macro economía resuelto para enseñar con
                claridad, seguridad y sentido real sin planificar nada desde
                cero.
              </p>
              <CTAButton className="w-full md:w-auto" data-id="element-11">
                Quiero mis clases listas
              </CTAButton>
            </div>
            <div className="flex-1 w-full max-w-md md:max-w-full" data-id="element-12">
              <img src="https://cdn.magicpatterns.com/uploads/31j6eoZ5aCvMWKyHovGyLs/conver_micro_y_macro.png" alt="Micro y Macro Economía 360 Pack" className="w-full h-auto drop-shadow-2xl hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={() => document.getElementById('comprar')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-13" />
            </div>
          </div>
        </div>

        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none" data-id="element-14">
          <svg className="relative block w-full h-[50px] md:h-[100px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" data-id="element-15">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-gray-50" data-id="element-16"></path>
          </svg>
        </div>
      </header>

      {/* PROBLEM IDENTIFICATION */}
      <section className="py-16 px-4 max-w-3xl mx-auto" data-id="element-17">
        <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-gray-100" data-id="element-18">
          <p className="text-lg md:text-xl font-medium text-blue-900 mb-6" data-id="element-19">
            Si sos docente de nivel secundario y alguna vez pensaste:
          </p>

          <div className="space-y-4 mb-8" data-id="element-20">
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border-l-4 border-red-400" data-id="element-21">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-22" />
              <p className="italic text-gray-700" data-id="element-23">
                "Mañana tengo que explicar inflación… y no sé cómo hacerlo
                simple"
              </p>
            </div>
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border-l-4 border-red-400" data-id="element-24">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-25" />
              <p className="italic text-gray-700" data-id="element-26">
                "Otra vez voy a improvisar"
              </p>
            </div>
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border-l-4 border-red-400" data-id="element-27">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-28" />
              <p className="italic text-gray-700" data-id="element-29">
                "Esto en el libro suena bien, pero en el aula no funciona"
              </p>
            </div>
          </div>

          <p className="text-xl font-bold text-gray-900 mb-4" data-id="element-30">Seguí leyendo.</p>
          <p className="text-gray-700 mb-4" data-id="element-31">
            No porque te falte capacidad. No porque no sepas economía. Sino
            porque{' '}
            <span className="font-bold bg-yellow-100 px-1" data-id="element-32">
              nadie te dio una herramienta pensada para tu realidad docente.
            </span>
          </p>
        </div>
      </section>

      {/* THE REAL PROBLEM */}
      <section className="py-12 px-4 max-w-4xl mx-auto text-center md:text-left" data-id="element-33">
        <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-8 text-center" data-id="element-34">
          El problema no es la economía.
          <br data-id="element-35" />
          <span className="text-red-500" data-id="element-36">
            El problema es cómo nos obligaron a enseñarla.
          </span>
        </h2>

        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-37">
          <div data-id="element-38">
            <p className="text-lg text-gray-700 mb-6" data-id="element-39">
              La economía está en todos lados:
            </p>
            <ul className="space-y-3 mb-8" data-id="element-40">
              {['En el precio del colectivo', 'En el sueldo que no alcanza', 'En la inflación que aparece en las noticias', 'En las decisiones que toman las familias todos los días'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-41">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-42" />
                  <span data-id="element-43">{item}</span>
                </li>)}
            </ul>

            <p className="text-lg text-gray-700 mb-4 font-medium" data-id="element-44">
              Y sin embargo… En el aula pasa esto:
            </p>
            <ul className="space-y-2 text-gray-600 mb-6 pl-4 border-l-2 border-gray-200" data-id="element-45">
              <li data-id="element-46">• Alumnos desconectados</li>
              <li data-id="element-47">• Miradas perdidas</li>
              <li data-id="element-48">• Celulares bajo el banco</li>
              <li data-id="element-49">
                • La pregunta inevitable:{' '}
                <span className="italic" data-id="element-50">"Profe… ¿esto para qué sirve?"</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 p-8 rounded-2xl border border-blue-100" data-id="element-51">
            <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-52">
              La trampa silenciosa del docente de economía
            </h3>
            <p className="text-gray-700 mb-4" data-id="element-53">
              Llegás a casa cansado. Terminás de corregir. Y todavía te queda:
            </p>
            <div className="space-y-2 mb-6" data-id="element-54">
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-55">
                <Clock className="w-4 h-4" data-id="element-56" /> Armar la próxima clase
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-57">
                <Clock className="w-4 h-4" data-id="element-58" /> Buscar ejemplos
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-59">
                <Clock className="w-4 h-4" data-id="element-60" /> Adaptar el contenido
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-61">
                <Clock className="w-4 h-4" data-id="element-62" /> Traducir el lenguaje del libro
              </div>
            </div>
            <p className="font-medium text-blue-800 mb-6" data-id="element-63">
              Horas de trabajo invisible. Horas que nadie ve. Horas que muchas
              veces no se notan en el aula.
            </p>
            <CTAButton fullWidth className="text-sm md:text-base py-3" data-id="element-64">
              Quiero terminar con esto hoy
            </CTAButton>
          </div>
        </div>
      </section>

      {/* SOLUTION REVEAL */}
      <section className="py-20 bg-blue-900 text-white px-4" data-id="element-65">
        <div className="max-w-4xl mx-auto text-center" data-id="element-66">
          <p className="text-amber-400 font-bold tracking-widest uppercase mb-4" data-id="element-67">
            La Solución
          </p>
          <h2 className="text-3xl md:text-5xl font-bold mb-8" data-id="element-68">
            Acá aparece el eslabón perdido:
            <br data-id="element-69" />
            Micro y Macro Economía 360
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mb-12 text-left" data-id="element-70">
            <div className="bg-blue-800 p-6 rounded-xl border border-blue-700 hover:border-amber-400 transition-colors" data-id="element-71">
              <Lightbulb className="w-10 h-10 text-amber-400 mb-4" data-id="element-72" />
              <h3 className="font-bold text-xl mb-2" data-id="element-73">Pedagogía Aplicada</h3>
              <p className="text-blue-200" data-id="element-74">
                No es solo información. Es un sistema que transforma la teoría
                del libro en realidad del aula.
              </p>
            </div>
            <div className="bg-blue-800 p-6 rounded-xl border border-blue-700 hover:border-amber-400 transition-colors" data-id="element-75">
              <Shield className="w-10 h-10 text-amber-400 mb-4" data-id="element-76" />
              <h3 className="font-bold text-xl mb-2" data-id="element-77">Seguridad Total</h3>
              <p className="text-blue-200" data-id="element-78">
                Llegás con todo listo. Sabés exactamente qué explicar y cómo
                hacerlo.
              </p>
            </div>
            <div className="bg-blue-800 p-6 rounded-xl border border-blue-700 hover:border-amber-400 transition-colors" data-id="element-79">
              <Users className="w-10 h-10 text-amber-400 mb-4" data-id="element-80" />
              <h3 className="font-bold text-xl mb-2" data-id="element-81">Conexión Real</h3>
              <p className="text-blue-200" data-id="element-82">
                Un traductor simultáneo entre la economía académica y el
                lenguaje del alumno.
              </p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 mb-12" data-id="element-83">
            <h3 className="text-2xl font-bold mb-6" data-id="element-84">
              ¿Qué es Micro y Macro Economía 360?
            </h3>
            <p className="text-lg mb-6" data-id="element-85">
              Es un{' '}
              <span className="text-amber-400 font-bold" data-id="element-86">
                pack completo de clases listas para usar
              </span>
              , creado exclusivamente para docentes de nivel secundario que
              dictan:
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-8" data-id="element-87">
              {['Economía', 'Microeconomía', 'Macroeconomía', 'Economía política', 'Ciencias sociales'].map(tag => <span key={tag} className="bg-blue-950 px-4 py-2 rounded-full text-sm font-medium border border-blue-700" data-id="element-88">
                  {tag}
                </span>)}
            </div>
            <p className="text-blue-200" data-id="element-89">
              Especialmente pensado para 4º, 5º y 6º año.
            </p>
          </div>

          <CTAButton data-id="element-90">Quiero acceder al material ahora</CTAButton>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-16 px-4 max-w-5xl mx-auto" data-id="element-91">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-12" data-id="element-92">
          ¿Qué incluye este pack?
        </h2>

        <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-93">
          <div className="space-y-6" data-id="element-94">
            {[{
            title: 'Clases estructuradas',
            desc: 'Listas para aplicar en el aula sin improvisar.'
          }, {
            title: 'Secuencia lógica y progresiva',
            desc: 'Un orden que tiene sentido para el alumno.'
          }, {
            title: 'Micro y Macro integradas',
            desc: 'Todo el contenido curricular cubierto.'
          }, {
            title: 'Ejemplos reales y actuales',
            desc: 'Casos que los chicos ven en su día a día.'
          }, {
            title: 'Lenguaje claro y didáctico',
            desc: 'Explicaciones que "bajan" la teoría a tierra.'
          }, {
            title: 'Material listo para usar',
            desc: 'Sin armar desde cero. Sin perder horas.'
          }].map((item, i) => <div key={i} className="flex gap-4" data-id="element-95">
                <div className="bg-green-100 p-2 rounded-full h-fit" data-id="element-96">
                  <Check className="w-5 h-5 text-green-600" data-id="element-97" />
                </div>
                <div data-id="element-98">
                  <h4 className="font-bold text-gray-900" data-id="element-99">{item.title}</h4>
                  <p className="text-gray-600" data-id="element-100">{item.desc}</p>
                </div>
              </div>)}
          </div>
          <div className="bg-gray-100 rounded-2xl p-6 flex items-center justify-center" data-id="element-101">
            <img src="https://cdn.magicpatterns.com/uploads/31j6eoZ5aCvMWKyHovGyLs/conver_micro_y_macro.png" alt="Pack Content Preview" className="w-full max-w-sm drop-shadow-xl transform rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-102" />
          </div>
        </div>

        {/* TRANSFORMATION */}
        <div className="bg-gradient-to-r from-gray-900 to-blue-900 text-white rounded-3xl p-8 md:p-12 shadow-xl" data-id="element-103">
          <h3 className="text-2xl md:text-3xl font-bold text-center mb-10" data-id="element-104">
            Cómo cambia tu día a día
          </h3>

          <div className="grid md:grid-cols-2 gap-12 relative" data-id="element-105">
            {/* Divider for desktop */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-700 transform -translate-x-1/2" data-id="element-106"></div>

            <div className="text-center md:text-right pr-0 md:pr-8 opacity-80" data-id="element-107">
              <h4 className="text-xl font-bold text-red-400 mb-6 uppercase tracking-widest" data-id="element-108">
                Antes
              </h4>
              <ul className="space-y-4" data-id="element-109">
                <li data-id="element-110">Llegabas a clase con dudas</li>
                <li data-id="element-111">Improvisabas ejemplos</li>
                <li data-id="element-112">Veías caras de confusión</li>
                <li data-id="element-113">Sentías inseguridad</li>
              </ul>
            </div>

            <div className="text-center md:text-left pl-0 md:pl-8" data-id="element-114">
              <h4 className="text-xl font-bold text-green-400 mb-6 uppercase tracking-widest" data-id="element-115">
                Ahora
              </h4>
              <ul className="space-y-4 font-medium" data-id="element-116">
                <li className="flex items-center justify-center md:justify-start gap-2" data-id="element-117">
                  <Check className="w-5 h-5 text-green-400" data-id="element-118" /> Llegás con todo
                  listo
                </li>
                <li className="flex items-center justify-center md:justify-start gap-2" data-id="element-119">
                  <Check className="w-5 h-5 text-green-400" data-id="element-120" /> Sabés exactamente
                  qué explicar
                </li>
                <li className="flex items-center justify-center md:justify-start gap-2" data-id="element-121">
                  <Check className="w-5 h-5 text-green-400" data-id="element-122" /> Los alumnos
                  entienden y participan
                </li>
                <li className="flex items-center justify-center md:justify-start gap-2" data-id="element-123">
                  <Check className="w-5 h-5 text-green-400" data-id="element-124" /> Volvés a sentirte
                  seguro y profesional
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-125">
            <p className="text-xl md:text-2xl font-serif italic text-amber-200 mb-8" data-id="element-126">
              "Podés dar un año completo de Micro y Macro Economía con clases
              claras, actuales y ordenadas sin volver a planificar desde cero"
            </p>
            <CTAButton data-id="element-127">Sí, quiero enseñar con claridad</CTAButton>
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-16 bg-blue-50 px-4" data-id="element-128">
        <div className="max-w-6xl mx-auto" data-id="element-129">
          <div className="text-center mb-12" data-id="element-130">
            <span className="bg-red-500 text-white font-bold px-4 py-1 rounded-full text-sm uppercase tracking-wider animate-pulse" data-id="element-131">
              Oferta Especial
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mt-4 mb-2" data-id="element-132">
              Bonos Incluidos
            </h2>
            <p className="text-xl text-amber-600 font-bold uppercase tracking-wide" data-id="element-133">
              GRATIS POR HOY
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-134">
            <BonusCard number={1} image="https://cdn.magicpatterns.com/uploads/cu7aRk3FU4iCqRegjy9aZJ/Bono_1_micro_y_macro.png" title="Banco de Ejemplos Reales Argentinos" description="La economía explicada con la vida cotidiana: inflación, dólar, supermercado." data-id="element-135" />
            <BonusCard number={2} image="https://cdn.magicpatterns.com/uploads/3ZvYzhpLHp3C7Tru633GSi/Cover_bono_2_micro_y_macro.png" title="Clases Express para Días Caóticos" description="Qué dar cuando no tuviste tiempo de preparar nada. Salvavidas pedagógicos." data-id="element-136" />
            <BonusCard number={3} image="https://cdn.magicpatterns.com/uploads/eiBHuotzo29iSMdUDoJqbW/BONO_3_MICRO_Y_MACRO.png" title="Evaluaciones Listas + Rúbricas" description="Evaluar sin perder horas. Pruebas escritas y trabajos prácticos listos." data-id="element-137" />
            <BonusCard number={4} image="https://cdn.magicpatterns.com/uploads/w7HkgJeH3LqzkxqAHqUPHW/BONO_4_micro_y_macro.png" title="Mapa Anual de Contenidos" description="Todo el año de economía en una sola hoja. Secuencia ideal." data-id="element-138" />
          </div>

          <div className="text-center" data-id="element-139">
            <CTAButton data-id="element-140">Quiero el pack completo + los bonos</CTAButton>
          </div>
        </div>
      </section>

      {/* PRICING / CHECKOUT */}
      <section id="comprar" className="py-20 bg-blue-900 text-white px-4 relative overflow-hidden" data-id="element-141">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-400 via-blue-900 to-blue-950" data-id="element-142"></div>

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-143">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-144">
            Empezá a enseñar con claridad hoy mismo
          </h2>
          <p className="text-xl text-blue-200 mb-10" data-id="element-145">
            Dejá de perder tiempo planificando. Volvé a disfrutar el aula.
          </p>

          <div className="bg-white text-gray-900 rounded-3xl p-8 md:p-12 shadow-2xl border-4 border-amber-400 mx-auto transform hover:scale-105 transition-transform duration-300" data-id="element-146">
            {/* Product Image */}
            <div className="mb-8 flex justify-center" data-id="element-147">
              <img src="https://cdn.magicpatterns.com/uploads/31j6eoZ5aCvMWKyHovGyLs/conver_micro_y_macro.png" alt="Micro y Macro Economía 360" className="w-full max-w-sm drop-shadow-2xl" data-id="element-148" />
            </div>

            <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2" data-id="element-149">
              Precio Final
            </div>
            <div className="flex items-center justify-center gap-2 mb-6" data-id="element-150">
              <span className="text-2xl text-gray-400 font-medium line-through" data-id="element-151">
                $49.990
              </span>
              <span className="text-6xl font-extrabold text-blue-900" data-id="element-152">
                $19.990
              </span>
            </div>

            <div className="space-y-4 mb-8 text-left max-w-md mx-auto" data-id="element-153">
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100" data-id="element-154">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-155" />
                <div data-id="element-156">
                  <span className="font-bold text-gray-900 block" data-id="element-157">
                    Pack Completo Micro y Macro Economía 360
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-158">
                    Clases estructuradas para todo el año lectivo con ejemplos
                    argentinos actuales
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-159">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-160" />
                <div data-id="element-161">
                  <span className="font-bold text-gray-900 block" data-id="element-162">
                    4 Bonos Exclusivos (Gratis por hoy)
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-163">
                    Banco de ejemplos, clases express, evaluaciones listas y
                    mapa anual de contenidos
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100" data-id="element-164">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-165" />
                <div data-id="element-166">
                  <span className="font-bold text-gray-900 block" data-id="element-167">
                    Acceso Inmediato y Vitalicio
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-168">
                    Descargá todo el material al instante y accedé cuando lo
                    necesites, sin vencimiento
                  </span>
                </div>
              </div>
            </div>

            <CTAButton href="https://checkout.rincondigital.shop/cart/52910368882963:1" fullWidth className="mb-4 text-base md:text-lg" data-id="element-169">
              Accedé ahora y empezá a enseñar
            </CTAButton>

            <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mt-4" data-id="element-170">
              <span className="flex items-center gap-1" data-id="element-171">
                <Shield className="w-3 h-3" data-id="element-172" /> Compra Segura
              </span>
              <span className="flex items-center gap-1" data-id="element-173">
                <BookOpen className="w-3 h-3" data-id="element-174" /> Material Digital
              </span>
            </div>
          </div>

          {/* UPSELL SECTION */}
          <div className="mt-16 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-6 md:p-12 border-4 border-amber-300 shadow-2xl" data-id="element-175">
            <div className="text-center mb-6 md:mb-8" data-id="element-176">
              <span className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold px-4 py-1.5 md:px-6 md:py-2 rounded-full text-xs md:text-sm uppercase tracking-wider shadow-lg animate-pulse" data-id="element-177">
                🔥 Oferta Especial - Pack Full
              </span>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mt-3 md:mt-4 mb-2" data-id="element-178">
                ¿Querés el Pack COMPLETO?
              </h3>
              <p className="text-base md:text-lg text-gray-700" data-id="element-179">
                Agregá Micro-Emprendimientos Ganadores y llevate TODO
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center" data-id="element-180">
              {/* Product Image */}
              <div className="flex justify-center" data-id="element-181">
                <img src="https://cdn.magicpatterns.com/uploads/xAjgHnUpYaAzxRwbxneGpU/ChatGPT_Image_11_ene_2026,_07_34_47_p.m..png" alt="Micro-Emprendimientos Ganadores" className="w-full max-w-xs md:max-w-sm drop-shadow-2xl rounded-2xl" data-id="element-182" />
              </div>

              {/* Upsell Details */}
              <div data-id="element-183">
                <div className="mb-4 md:mb-6" data-id="element-184">
                  <div className="text-xs md:text-sm font-bold text-gray-500 uppercase tracking-widest mb-2" data-id="element-185">
                    Pack Full - Precio Especial
                  </div>
                  <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4" data-id="element-186">
                    <span className="text-xl md:text-2xl text-gray-400 font-medium line-through" data-id="element-187">
                      $59.990
                    </span>
                    <span className="text-4xl md:text-5xl font-extrabold text-orange-600" data-id="element-188">
                      $26.990
                    </span>
                  </div>
                </div>

                <div className="space-y-2.5 md:space-y-3 mb-6 md:mb-8" data-id="element-189">
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-white rounded-lg border border-green-200 shadow-sm" data-id="element-190">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-191" />
                    <div data-id="element-192">
                      <span className="font-bold text-gray-900 block text-sm md:text-base" data-id="element-193">
                        Pack Completo Micro y Macro Economía 360
                      </span>
                      <span className="text-xs md:text-sm text-gray-600" data-id="element-194">
                        Clases estructuradas para todo el año lectivo con
                        ejemplos argentinos actuales
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-white rounded-lg border border-amber-200 shadow-sm" data-id="element-195">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-196" />
                    <div data-id="element-197">
                      <span className="font-bold text-gray-900 block text-sm md:text-base" data-id="element-198">
                        4 Bonos Exclusivos (Gratis por hoy)
                      </span>
                      <span className="text-xs md:text-sm text-gray-600" data-id="element-199">
                        Banco de ejemplos, clases express, evaluaciones listas y
                        mapa anual de contenidos
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-white rounded-lg border border-blue-200 shadow-sm" data-id="element-200">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-201" />
                    <div data-id="element-202">
                      <span className="font-bold text-gray-900 block text-sm md:text-base" data-id="element-203">
                        Acceso Inmediato y Vitalicio
                      </span>
                      <span className="text-xs md:text-sm text-gray-600" data-id="element-204">
                        Descargá todo el material al instante y accedé cuando lo
                        necesites, sin vencimiento
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-gradient-to-r from-orange-100 to-amber-100 rounded-lg border-2 border-orange-300 shadow-md" data-id="element-205">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-orange-600 flex-shrink-0 mt-0.5" data-id="element-206" />
                    <div data-id="element-207">
                      <span className="font-bold text-orange-900 block text-sm md:text-base" data-id="element-208">
                        🎁 EXTRA: Micro-Emprendimientos Ganadores
                      </span>
                      <span className="text-xs md:text-sm text-orange-800 font-medium" data-id="element-209">
                        100 ideas reales de emprendimientos para trabajar con
                        alumnos de último año
                      </span>
                    </div>
                  </div>
                </div>

                <a href="https://checkout.rincondigital.shop/cart/52912857252115:1" className="inline-flex items-center justify-center gap-2 w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold py-3.5 md:py-4 px-6 md:px-8 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 text-base md:text-lg uppercase tracking-wide" data-id="element-210">
                  Accedé al Pack Full
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-211">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" data-id="element-212" />
                  </svg>
                </a>

                <p className="text-center text-xs md:text-sm text-gray-600 mt-3 md:mt-4" data-id="element-213">
                  💎 Ahorrás $33.000 con esta oferta especial
                </p>
              </div>
            </div>
          </div>

          {/* SECOND UPSELL - PACK FULL 360 */}
          <div className="mt-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-6 md:p-12 border-4 border-blue-300 shadow-2xl" data-id="element-214">
            <div className="text-center mb-6 md:mb-8" data-id="element-215">
              <span className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-4 py-1.5 md:px-6 md:py-2 rounded-full text-xs md:text-sm uppercase tracking-wider shadow-lg animate-pulse" data-id="element-216">
                🔥 Oferta Especial - Pack Full 360
              </span>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mt-3 md:mt-4 mb-2" data-id="element-217">
                Llevate TODO el Sistema 360
              </h3>
              <p className="text-base md:text-lg text-gray-700" data-id="element-218">
                SIC 360, Micro y Macro Economía 360 + 100 ideas de
                emprendimientos
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center" data-id="element-219">
              {/* Carousel */}
              <div className="relative" data-id="element-220">
                <div className="overflow-hidden rounded-2xl shadow-2xl" data-id="element-221">
                  <div className="flex transition-transform duration-500 ease-in-out" id="carousel-full-360" data-id="element-222">
                    <img src="https://cdn.magicpatterns.com/uploads/aUcdZDr96ZbqovVX9TrgaS/Cover_SIC_360.png" alt="SIC 360" className="w-full flex-shrink-0 object-cover" data-id="element-223" />
                    <img src="https://cdn.magicpatterns.com/uploads/81m1Z7LDdrDKDYnzWA8MTr/ChatGPT_Image_10_ene_2026,_11_41_04_p.m..png" alt="Micro y Macro Economía 360" className="w-full flex-shrink-0 object-cover" data-id="element-224" />
                    <img src="https://cdn.magicpatterns.com/uploads/uueHQGTE48TQH7sjyua3i4/ChatGPT_Image_11_ene_2026,_07_34_47_p.m..png" alt="Micro-Emprendimientos Ganadores" className="w-full flex-shrink-0 object-cover" data-id="element-225" />
                  </div>
                </div>
                {/* Carousel indicators */}
                <div className="flex justify-center gap-2 mt-4" data-id="element-226">
                  <button className="w-2 h-2 rounded-full bg-blue-600" data-id="element-227"></button>
                  <button className="w-2 h-2 rounded-full bg-gray-300" data-id="element-228"></button>
                  <button className="w-2 h-2 rounded-full bg-gray-300" data-id="element-229"></button>
                </div>
              </div>

              {/* Upsell Details */}
              <div data-id="element-230">
                <div className="mb-4 md:mb-6" data-id="element-231">
                  <div className="text-xs md:text-sm font-bold text-gray-500 uppercase tracking-widest mb-2" data-id="element-232">
                    Pack Full 360 - Precio Especial
                  </div>
                  <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4" data-id="element-233">
                    <span className="text-xl md:text-2xl text-gray-400 font-medium line-through" data-id="element-234">
                      $69.990
                    </span>
                    <span className="text-4xl md:text-5xl font-extrabold text-blue-600" data-id="element-235">
                      $39.990
                    </span>
                  </div>
                  <p className="text-green-600 font-bold text-sm md:text-base" data-id="element-236">
                    💰 Ahorrás $30.000 con el pack completo
                  </p>
                </div>

                <div className="space-y-2.5 md:space-y-3 mb-6 md:mb-8" data-id="element-237">
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-white rounded-lg border border-green-200 shadow-sm" data-id="element-238">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-239" />
                    <div data-id="element-240">
                      <span className="font-bold text-gray-900 block text-sm md:text-base" data-id="element-241">
                        Pack Completo Micro y Macro Economía 360
                      </span>
                      <span className="text-xs md:text-sm text-gray-600" data-id="element-242">
                        Clases estructuradas para todo el año lectivo con
                        ejemplos argentinos actuales
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-white rounded-lg border border-amber-200 shadow-sm" data-id="element-243">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-244" />
                    <div data-id="element-245">
                      <span className="font-bold text-gray-900 block text-sm md:text-base" data-id="element-246">
                        4 Bonos Exclusivos (Gratis por hoy)
                      </span>
                      <span className="text-xs md:text-sm text-gray-600" data-id="element-247">
                        Banco de ejemplos, clases express, evaluaciones listas y
                        mapa anual de contenidos
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-white rounded-lg border border-blue-200 shadow-sm" data-id="element-248">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-249" />
                    <div data-id="element-250">
                      <span className="font-bold text-gray-900 block text-sm md:text-base" data-id="element-251">
                        Acceso Inmediato y Vitalicio
                      </span>
                      <span className="text-xs md:text-sm text-gray-600" data-id="element-252">
                        Descargá todo el material al instante y accedé cuando lo
                        necesites, sin vencimiento
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg border-2 border-blue-400 shadow-md" data-id="element-253">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-254" />
                    <div data-id="element-255">
                      <span className="font-bold text-blue-900 block text-sm md:text-base" data-id="element-256">
                        🎁 EXTRA: SIC 360 + 100 MICRO-EMPRENDIMIENTOS GANADORES
                      </span>
                      <span className="text-xs md:text-sm text-blue-800 font-medium" data-id="element-257">
                        El pack completo de SIC 360 + micro y macro economía 360
                        + 100 ideas reales de emprendimientos para trabajar con
                        alumnos de último año
                      </span>
                    </div>
                  </div>
                </div>

                <a href="https://checkout.rincondigital.shop/cart/52917897363731:1" className="inline-flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3.5 md:py-4 px-6 md:px-8 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 text-base md:text-lg uppercase tracking-wide" data-id="element-258">
                  Accedé al Pack Full 360
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-259">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" data-id="element-260" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* THIRD UPSELL - MEGAPACK COMERCIAL CONTABLE 360 */}
          <div className="mt-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-6 md:p-12 border-4 border-purple-300 shadow-2xl" data-id="element-261">
            <div className="text-center mb-6 md:mb-8" data-id="element-262">
              <span className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold px-4 py-1.5 md:px-6 md:py-2 rounded-full text-xs md:text-sm uppercase tracking-wider shadow-lg animate-pulse" data-id="element-263">
                🔥 Oferta Especial - Megapack Comercial Contable
              </span>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mt-3 md:mt-4 mb-2" data-id="element-264">
                Llevate TODO el Megapack Comercial Contable 360
              </h3>
              <p className="text-base md:text-lg text-gray-700" data-id="element-265">
                NTICX 360 + SIC 360 + Micro y Macro Economía 360
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center" data-id="element-266">
              {/* Carousel */}
              <div className="relative" data-id="element-267">
                <div className="overflow-hidden rounded-2xl shadow-2xl" data-id="element-268">
                  <div className="flex transition-transform duration-500 ease-in-out" id="carousel-megapack" data-id="element-269">
                    <img src="https://cdn.magicpatterns.com/uploads/339fpqmaudVT1Kc8vsAV17/COVER_NTICX360.png" alt="NTICX 360" className="w-full flex-shrink-0 object-cover" data-id="element-270" />
                    <img src="https://cdn.magicpatterns.com/uploads/9pUgTWv6oBn4oq4gw2HFde/Cover_SIC_360.png" alt="SIC 360" className="w-full flex-shrink-0 object-cover" data-id="element-271" />
                    <img src="https://cdn.magicpatterns.com/uploads/6enccKSFc8kR3sDyqNY5R4/conver_micro_y_macro.png" alt="Micro y Macro Economía 360" className="w-full flex-shrink-0 object-cover" data-id="element-272" />
                  </div>
                </div>
                {/* Carousel indicators */}
                <div className="flex justify-center gap-2 mt-4" data-id="element-273">
                  <button className="w-2 h-2 rounded-full bg-purple-600" data-id="element-274"></button>
                  <button className="w-2 h-2 rounded-full bg-gray-300" data-id="element-275"></button>
                  <button className="w-2 h-2 rounded-full bg-gray-300" data-id="element-276"></button>
                </div>
              </div>

              {/* Upsell Details */}
              <div data-id="element-277">
                <div className="mb-4 md:mb-6" data-id="element-278">
                  <div className="text-xs md:text-sm font-bold text-gray-500 uppercase tracking-widest mb-2" data-id="element-279">
                    Megapack Comercial Contable - Precio Especial
                  </div>
                  <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4" data-id="element-280">
                    <span className="text-xl md:text-2xl text-gray-400 font-medium line-through" data-id="element-281">
                      $79.990
                    </span>
                    <span className="text-4xl md:text-5xl font-extrabold text-purple-600" data-id="element-282">
                      $49.990
                    </span>
                  </div>
                  <p className="text-green-600 font-bold text-sm md:text-base" data-id="element-283">
                    💰 Ahorrás $30.000 con el megapack completo
                  </p>
                </div>

                <div className="space-y-2.5 md:space-y-3 mb-6 md:mb-8" data-id="element-284">
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-white rounded-lg border border-green-200 shadow-sm" data-id="element-285">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-286" />
                    <div data-id="element-287">
                      <span className="font-bold text-gray-900 block text-sm md:text-base" data-id="element-288">
                        Pack Completo Micro y Macro Economía 360
                      </span>
                      <span className="text-xs md:text-sm text-gray-600" data-id="element-289">
                        Clases estructuradas para todo el año lectivo con
                        ejemplos argentinos actuales
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-white rounded-lg border border-amber-200 shadow-sm" data-id="element-290">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-291" />
                    <div data-id="element-292">
                      <span className="font-bold text-gray-900 block text-sm md:text-base" data-id="element-293">
                        4 Bonos Exclusivos (Gratis por hoy)
                      </span>
                      <span className="text-xs md:text-sm text-gray-600" data-id="element-294">
                        Banco de ejemplos, clases express, evaluaciones listas y
                        mapa anual de contenidos
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-white rounded-lg border border-blue-200 shadow-sm" data-id="element-295">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-296" />
                    <div data-id="element-297">
                      <span className="font-bold text-gray-900 block text-sm md:text-base" data-id="element-298">
                        Acceso Inmediato y Vitalicio
                      </span>
                      <span className="text-xs md:text-sm text-gray-600" data-id="element-299">
                        Descargá todo el material al instante y accedé cuando lo
                        necesites, sin vencimiento
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg border-2 border-purple-400 shadow-md" data-id="element-300">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-purple-600 flex-shrink-0 mt-0.5" data-id="element-301" />
                    <div data-id="element-302">
                      <span className="font-bold text-purple-900 block text-sm md:text-base" data-id="element-303">
                        🎁 EXTRA: NTICX 360 + SIC 360 + MICRO Y MACRO ECONOMÍA
                        360
                      </span>
                      <span className="text-xs md:text-sm text-purple-800 font-medium" data-id="element-304">
                        Accede al megapack comercial contable completo con
                        clases resueltas para todo el año
                      </span>
                    </div>
                  </div>
                </div>

                <a href="https://checkout.rincondigital.shop/cart/52921028411667:1" className="inline-flex items-center justify-center gap-2 w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3.5 md:py-4 px-6 md:px-8 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 text-base md:text-lg uppercase tracking-wide" data-id="element-305">
                  Accedé al Megapack Comercial Contable 360
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-306">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" data-id="element-307" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 opacity-70 text-sm" data-id="element-308">
            <p data-id="element-309">
              © 2026 Micro y Macro Economía 360. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </section>

      {/* GUARANTEE SECTION */}
      <section className="py-16 px-4 bg-gradient-to-b from-blue-900 to-blue-950" data-id="element-310">
        <div className="max-w-4xl mx-auto text-center" data-id="element-311">
          <div className="flex justify-center mb-6" data-id="element-312">
            <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center shadow-xl" data-id="element-313">
              <Shield className="w-10 h-10 text-white" strokeWidth={2.5} data-id="element-314" />
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-315">
            Garantía sin vueltas
          </h2>

          <p className="text-lg md:text-xl text-blue-100 mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-316">
            Estamos tan seguros de que este Programa te va a encantar, que si no
            es así, te devolvemos el 100% de tu dinero.
          </p>

          <p className="text-base md:text-lg text-blue-200 max-w-2xl mx-auto leading-relaxed" data-id="element-317">
            Tenés 7 días para probarlo sin riesgo. Si no te convence, nos
            escribís y te hacemos el reembolso. Así de simple.
          </p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 max-w-6xl mx-auto bg-gray-50" data-id="element-318">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-12" data-id="element-319">
          Lo que dicen otros docentes
        </h2>

        <div className="grid md:grid-cols-3 gap-6 mb-8" data-id="element-320">
          <TestimonialCard name="María Fernanda López" age={42} city="Buenos Aires" quote="Después de 15 años dando economía, este material cambió completamente mi forma de enseñar. Mis alumnos ahora participan y entienden. Ya no improviso más." data-id="element-321" />
          <TestimonialCard name="Roberto Carlos Martínez" age={38} city="Córdoba" quote="Lo que más valoro es el tiempo que me ahorra. Llego a casa y puedo estar con mi familia en vez de planificar hasta las 11 de la noche. Las clases están impecables." data-id="element-322" />
          <TestimonialCard name="Silvana Beatriz Rodríguez" age={51} city="Rosario" quote="Pensé que ya lo había visto todo en educación. Pero este pack me devolvió las ganas de enseñar. Los ejemplos argentinos son oro puro para conectar con los chicos." data-id="element-323" />
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-324">
          <TestimonialCard name="Gustavo Hernán Pérez" age={45} city="Mendoza" quote="Soy docente hace 20 años y siempre me costó explicar macro. Con este material, hasta los conceptos más complejos se vuelven claros. Mis alumnos aprueban más y mejor." data-id="element-325" />
          <TestimonialCard name="Laura Cecilia Gómez" age={39} city="La Plata" quote="Compré este pack con dudas, pero fue la mejor inversión del año. Ahora entro al aula segura, con todo preparado. Mis clases tienen otra calidad." data-id="element-326" />
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-16 px-4 max-w-4xl mx-auto" data-id="element-327">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-4" data-id="element-328">
          Resolvé aquí tus DUDAS
        </h2>
        <p className="text-center text-gray-600 mb-12" data-id="element-329">
          Las preguntas más frecuentes sobre el pack
        </p>

        <div className="space-y-4" data-id="element-330">
          {[{
          question: '¿Qué recibo al comprar el Kit?',
          answer: 'Recibís el pack completo de Micro y Macro Economía 360 con todas las clases estructuradas para el año lectivo, más los 4 bonos exclusivos: Banco de Ejemplos Reales Argentinos, Clases Express para Días Caóticos, Evaluaciones Listas + Rúbricas, y Mapa Anual de Contenidos.'
        }, {
          question: '¿Cuándo me llega?',
          answer: 'El acceso es inmediato. Apenas confirmás tu compra, recibís un email con el link de descarga y podés empezar a usar el material al instante.'
        }, {
          question: '¿Cómo lo recibo?',
          answer: 'Te llega por email un link de acceso a una plataforma donde podés descargar todo el material en formato digital (PDF). Es simple, rápido y seguro.'
        }, {
          question: '¿Es físico o digital?',
          answer: 'Es 100% digital. Esto te permite acceder desde cualquier dispositivo, descargarlo cuantas veces quieras y tenerlo siempre disponible sin depender de envíos.'
        }, {
          question: '¿Necesito ser profesional?',
          answer: 'No necesitás título específico. El material está diseñado para cualquier docente que dicte economía en nivel secundario, sin importar tu formación de base.'
        }, {
          question: '¿Tengo acceso para siempre?',
          answer: 'Sí, el acceso es vitalicio. Una vez que comprás, el material es tuyo para siempre. Podés descargarlo y usarlo todos los años que quieras, sin vencimiento.'
        }, {
          question: '¿Qué hago si no me llega el acceso?',
          answer: 'Revisá tu carpeta de spam o correo no deseado. Si no lo encontrás, escribinos y te reenviamos el acceso en minutos. Estamos para ayudarte.'
        }, {
          question: '¿Es una compra segura?',
          answer: 'Totalmente segura. Usamos plataformas de pago certificadas que protegen tus datos. Además, tenés garantía de 7 días: si no te convence, te devolvemos el 100% de tu dinero.'
        }].map((faq, index) => <details key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group" data-id="element-331">
              <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-blue-50 transition-colors" data-id="element-332">
                <span className="font-bold text-gray-900 pr-4" data-id="element-333">
                  {faq.question}
                </span>
                <svg className="w-5 h-5 text-blue-900 transform group-open:rotate-180 transition-transform flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-334">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-335" />
                </svg>
              </summary>
              <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-4" data-id="element-336">
                {faq.answer}
              </div>
            </details>)}
        </div>

        <div className="mt-12 text-center" data-id="element-337">
          <CTAButton data-id="element-338">Quiero acceder ahora sin riesgo</CTAButton>
        </div>
      </section>
    </div>;
}